/**
 * Cotação conjunta (2026-10-02): o comprador registra a proposta de UM
 * fornecedor para vários itens de uma vez — um frete para o pedido (rateado
 * pelo valor de cada item) e um prazo geral, com exceção por item.
 *
 * Também é o formulário de UM item ("+ Fornecedor" no item abre aqui com ele
 * só): um jeito de cotar, não dois. Se o fornecedor já cotou um item, a linha
 * vem preenchida e o que mudar substitui.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiDollarSign } from "react-icons/fi";
import styled from "styled-components";

import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { cotarEmConjunto, listarFornecedores } from "../../../services/compras";
import { formatarReaisBR } from "../../../utils/dinheiro";
import { fmtQt } from "../../RevisorFerragens/lib/documento";
import { Mono } from "../_shared/quadro/styles";
import { linhasIniciais, payloadDaCotacao, previaDaCotacao, qtdDoItem } from "./regrasDaCotacao";
import { nomeDoItem } from "./ItensDaRequisicao";
import { ModalCaixa, ModalOverlay, Rotulo } from "./ui";

const Caixa = styled(ModalCaixa)`
  width: min(100%, 860px);
  max-height: calc(100vh - 32px);
  overflow: auto;
`;

const Campos = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  @media (max-width: 640px) { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); > :first-child { grid-column: 1 / -1; } }
`;

const Grade = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  font-size: 12.5px;
  > .linha {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) 110px 76px 76px 96px 104px;
    gap: 8px; align-items: center; padding: 6px 10px; min-width: 0;
    border-bottom: 1px solid var(--border-subtle);
  }
  > .linha:last-child { border-bottom: 0; }
  > .linha > div { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  > .cab { background: var(--surface-2); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: var(--ink-3); }
  > .rodape { background: var(--surface-2); font-weight: 600; }
  .num { text-align: right; }
  @media (max-width: 640px) {
    > .linha { grid-template-columns: minmax(0, 1fr) 96px 64px; }
    > .linha > .esc { display: none; }
  }
`;

const CAMPO = {
  height: 30, padding: "0 8px", borderRadius: 8, border: "1px solid var(--border)",
  background: "var(--surface)", color: "var(--ink)", font: "inherit", fontSize: 12.5, width: "100%", boxSizing: "border-box",
};

const dinheiro = (v) => (v === null || v === undefined ? "—" : formatarReaisBR(v));

export default function CotacaoConjunta({ req, itens, fornecedorInicial = "", onFechar, onSalvo }) {
  const { request } = useAuth();
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedor, setFornecedor] = useState(fornecedorInicial);
  const [prazoGeral, setPrazoGeral] = useState("");
  const [frete, setFrete] = useState("");
  const [observacao, setObservacao] = useState("");
  const [linhas, setLinhas] = useState(() => linhasIniciais(itens, fornecedorInicial));
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    listarFornecedores(request, { pageSize: 500, ativo: true })
      .then((p) => { if (vivo) setFornecedores(Array.isArray(p) ? p : p?.results || []); })
      .catch(() => { if (vivo) setFornecedores([]); });
    return () => { vivo = false; };
  }, [request]);

  const previa = useMemo(() => previaDaCotacao(linhas, frete), [linhas, frete]);

  function trocarFornecedor(id) {
    setFornecedor(id);
    setLinhas(linhasIniciais(itens, id));
    setErro("");
  }

  const mudarLinha = (i, campo, valor) => setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));

  async function salvar() {
    const { erro: problema, payload } = payloadDaCotacao({ fornecedor, prazoGeral, frete, observacao, linhas });
    if (problema) { setErro(problema); return; }
    setErro("");
    setSalvando(true);
    try {
      const detalhe = await cotarEmConjunto(request, req.id, payload);
      await onSalvo?.(detalhe);
    } catch (e) {
      setErro(e?.payload?.detalhe || e?.message || "Não foi possível registrar a cotação.");
    } finally {
      setSalvando(false);
    }
  }

  const jaCotados = linhas.filter((l) => l.jaCotado).length;

  return (
    <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget && !salvando) onFechar?.(); }}>
      <Caixa role="dialog" aria-modal="true" aria-labelledby="cotacao-conjunta-titulo">
        <h3 id="cotacao-conjunta-titulo">
          <FiDollarSign size={18} style={{ color: "var(--accent)" }} />
          {itens.length === 1 ? `Cotar ${nomeDoItem(itens[0])}` : `Cotar ${itens.length} itens juntos`}
        </h3>
        <Campos>
          <label>
            <Rotulo>Fornecedor</Rotulo>
            <select value={fornecedor} onChange={(e) => trocarFornecedor(e.target.value)} style={CAMPO} aria-label="Fornecedor" disabled={salvando}>
              <option value="">— Escolha —</option>
              {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </label>
          <label>
            <Rotulo>Prazo geral (dias)</Rotulo>
            <input value={prazoGeral} onChange={(e) => setPrazoGeral(e.target.value)} inputMode="numeric" placeholder="ex.: 5" style={CAMPO} aria-label="Prazo geral em dias" disabled={salvando} />
          </label>
          <label>
            <Rotulo>Frete do pedido</Rotulo>
            <input value={frete} onChange={(e) => setFrete(e.target.value)} inputMode="decimal" placeholder="R$ — rateado" style={{ ...CAMPO, fontFamily: "var(--font-mono)" }} aria-label="Frete total" disabled={salvando} />
          </label>
        </Campos>
        {fornecedor && jaCotados ? (
          <p>{jaCotados === 1 ? "1 item já tinha" : `${jaCotados} itens já tinham`} cotação deste fornecedor: o que você mudar substitui.</p>
        ) : null}
        <Grade>
          <div className="linha cab">
            <div>Item</div><div>Unitário</div><div>Não tem</div><div className="esc">Prazo</div>
            <div className="num esc">Frete</div><div className="num">Total</div>
          </div>
          {linhas.map((l, i) => (
            <div className="linha" key={l.item.id}>
              <div title={nomeDoItem(l.item)}>
                <span style={{ fontWeight: 600 }}>{nomeDoItem(l.item)}</span>
                <span style={{ color: "var(--ink-3)" }}> ×{fmtQt(qtdDoItem(l.item))}</span>
              </div>
              <div>
                <input
                  value={l.naoTem ? "" : l.valor}
                  onChange={(e) => mudarLinha(i, "valor", e.target.value)}
                  disabled={l.naoTem || salvando}
                  inputMode="decimal" placeholder="R$" aria-label={`Valor unitário de ${nomeDoItem(l.item)}`}
                  style={{ ...CAMPO, fontFamily: "var(--font-mono)" }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <input
                  type="checkbox" checked={l.naoTem} disabled={salvando}
                  onChange={(e) => mudarLinha(i, "naoTem", e.target.checked)}
                  aria-label={`${nomeDoItem(l.item)}: fornecedor não tem`}
                />
              </div>
              <div className="esc">
                <input
                  value={l.prazo} onChange={(e) => mudarLinha(i, "prazo", e.target.value)}
                  disabled={l.naoTem || salvando} inputMode="numeric"
                  placeholder={prazoGeral || "dias"} title="Vazio = prazo geral"
                  aria-label={`Prazo de ${nomeDoItem(l.item)}`} style={CAMPO}
                />
              </div>
              <div className="num esc"><Mono $size={12}>{l.naoTem ? "—" : dinheiro(previa.linhas[i].frete)}</Mono></div>
              <div className="num"><Mono $size={12}>{l.naoTem ? "não tem" : dinheiro(previa.linhas[i].total)}</Mono></div>
            </div>
          ))}
          <div className="linha rodape">
            <div>Total do fornecedor</div><div /><div /><div className="esc" /><div className="esc" />
            <div className="num"><Mono $size={12}>{formatarReaisBR(previa.total)}</Mono></div>
          </div>
        </Grade>
        <p>
          Frete em branco mantém o que já estava gravado em cada item; preenchido, é dividido pelo valor de cada
          item. Prazo em branco na linha usa o prazo geral.
        </p>
        <label>
          <Rotulo>Observação (opcional)</Rotulo>
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={300} style={CAMPO} aria-label="Observação" disabled={salvando} />
        </label>
        {erro ? <p role="alert" style={{ color: "var(--danger-text)" }}>{erro}</p> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--secondary" onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn btn--primary" onClick={salvar} disabled={salvando || !fornecedor}>
            {salvando ? <MiniSpinner size={13} label="Salvando" /> : null}
            Salvar cotação
          </button>
        </div>
      </Caixa>
    </ModalOverlay>
  );
}
